import { useEffect, useState } from "react";
import SearchWidget from "@/components/SearchWidget";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { ShieldCheck, Wallet, Sparkles, ParkingSquare } from "lucide-react";

export default function Home() {
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    api.get("/airports?popular=true").then((r) => setPopular(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--fpn-navy)]">
        <div
          className="absolute inset-0 opacity-25 bg-cover bg-center pointer-events-none"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/19190599/pexels-photo-19190599.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }}
        />
        <div className="absolute inset-0 grain opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-40 lg:pb-52 text-white">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[var(--fpn-amber)]" data-testid="hero-eyebrow">
              <ParkingSquare size={14} /> One search. Every way to park.
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-[1.05]" data-testid="hero-headline">
              Find the right airport parking <span className="text-[var(--fpn-amber)]">for less.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-200 max-w-xl leading-relaxed" data-testid="hero-sub">
              Compare official airport parking, Park &amp; Ride, Meet &amp; Greet and approved local parking spaces in one search.
            </p>
          </div>
        </div>
      </section>

      {/* Search overlap */}
      <section className="relative -mt-32 lg:-mt-40">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <SearchWidget />
        </div>
      </section>

      {/* Popular airports */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500">Popular</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-[var(--fpn-navy)] mt-2">Popular UK airports</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {popular.map((a) => (
            <Link
              key={a.iata_code}
              to={`/search?airport=${a.iata_code}`}
              data-testid={`popular-airport-${a.iata_code}`}
              className="card-lift group border border-slate-200 rounded-2xl p-5 bg-white"
            >
              <div className="text-2xl font-display font-bold text-[var(--fpn-navy)]">{a.iata_code}</div>
              <div className="text-sm text-slate-600 mt-1">{a.city}</div>
              <div className="mt-4 h-px bg-slate-100" />
              <div className="mt-3 text-xs font-semibold text-[var(--fpn-navy)] group-hover:text-[var(--fpn-amber-2)]">Compare parking →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Why */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-24 grid md:grid-cols-3 gap-6">
        {[
          { icon: Sparkles, title: "Compare more", body: "Official, commercial and community parking, side by side." },
          { icon: Wallet, title: "Know the total", body: "Clear all-in pricing before you book. No surprise fees." },
          { icon: ShieldCheck, title: "Park with confidence", body: "Approved community listings and secure payments." },
        ].map((f, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 p-8 bg-white card-lift" data-testid={`why-card-${i}`}>
            <f.icon className="text-[var(--fpn-amber-2)]" />
            <div className="font-display text-xl font-semibold mt-4 text-[var(--fpn-navy)]">{f.title}</div>
            <p className="text-slate-600 mt-2 text-sm leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      {/* Host CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 mt-24">
        <div className="rounded-3xl bg-[var(--fpn-navy)] text-white p-10 md:p-14 grid md:grid-cols-2 gap-8 items-center overflow-hidden relative">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[var(--fpn-amber)]">For Hosts</div>
            <h3 className="font-display text-3xl md:text-4xl font-semibold mt-3 leading-tight">Live near an airport?<br/>Turn your unused parking space into income.</h3>
            <p className="text-slate-300 mt-4 max-w-md text-sm">Simple onboarding. You stay in control of availability. Get paid after every completed booking.</p>
            <Link to="/host" data-testid="host-cta-button" className="btn-amber inline-block mt-6 rounded-full px-6 py-3 text-sm">List Your Space</Link>
          </div>
          <div className="hidden md:block">
            <img src="https://images.pexels.com/photos/8696219/pexels-photo-8696219.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Secure parking" className="rounded-2xl object-cover w-full h-64" />
          </div>
        </div>
      </section>
    </div>
  );
}
